/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-shadow */
import { Select, useDisclosure, Spinner, Center, Box } from '@chakra-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { QuestionnaireTemplateModal } from '../modal/QuestionnaireTemplateModal';
import {
  PaginationQueryType,
  QuestionnaireTemplateListResponse,
  QuestionTemplatePagination,
  SortDirection,
  SortField,
} from '../questionnaireTemplate.types';
import { QuestionnaireTemplateEmptyView } from './QuestionnaireTemplateEmptyView';
import QuestionnaireTemplateTableView from './QuestionnaireTemplateTableView';

const PAGE_SIZE = 10;

const PAGINATION_LIMIT_KEY = 'questionnaireTemplatesPaginationLimit';

const defaultPagination: PaginationQueryType = {
  page: 1,
  limit: Number(localStorage.getItem(PAGINATION_LIMIT_KEY)) || PAGE_SIZE,
  before: null,
  after: null,
  type: 'default',
  total: 0,
  hasNext: false,
  hasPrevious: false,
};

export interface PaginationLimitProps {
  limit: number | null;
  onChange: (limit: number) => void;
}

export const QuestionnaireTemplateView = () => {
  const [templateResponse, setTemplateResponse] = useState<QuestionTemplatePagination>();
  const [showDefaultTemplates, setShowDefaultTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<QuestionnaireTemplateListResponse | null>(null);
  const [sortField, setSortField] = useState<SortField>('positionCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [paginationQuery, setPaginationQuery] = useState<PaginationQueryType>(() => defaultPagination);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [hasDefaultTemplates, setHasDefaultTemplates] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const accountId = useStoreState((s) => s.app.user?.account);
  const fetchQuestionTemplates = useStoreActions((actions) => actions.account.questionTemplateList);

  const handleFetchTemplates = useCallback(
    (pagination: PaginationQueryType, field: SortField, direction: SortDirection) => {
      if (!accountId) return;
      setIsFetching(true);

      fetchQuestionTemplates({
        accountId,
        ...pagination,
        sortField: field,
        sortDirection: direction,
      }).then((res) => {
        const { data } = res;

        setTemplateResponse(data);

        // Detect default templates from the main fetch — no separate request needed
        if (data?.templates.some((template) => template.default)) {
          setHasDefaultTemplates(true);
        }

        setPaginationQuery((prev) => ({
          ...prev,
          hasNext: data?.hasNext ?? false,
          hasPrevious: data?.hasPrevious ?? false,
          total: data?.total ?? 0,
          page: data?.page ?? prev.page,
        }));
        setIsFetching(false);
        setIsInitialLoading(false);
      });
    },
    [accountId, fetchQuestionTemplates],
  );

  // Single fetch effect — no redundant "type: default" prefetch needed
  useEffect(() => {
    handleFetchTemplates(paginationQuery, sortField, sortDirection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleFetchTemplates,
    sortField,
    sortDirection,
    paginationQuery.page,
    paginationQuery.limit,
    paginationQuery.type,
    paginationQuery.searchQuery,
  ]);

  useEffect(() => {
    setIsFetching(true);
    setPaginationQuery((prev) => ({
      ...prev,
      before: null,
      after: null,
      page: 1,
      searchQuery: debouncedSearchQuery || undefined,
    }));
  }, [debouncedSearchQuery]);

  // Toggle effect: type: 'default' when on, type: null when off
  useEffect(() => {
    setPaginationQuery((prev) => ({
      ...prev,
      before: null,
      after: null,
      page: 1,
      type: showDefaultTemplates ? 'default' : null,
    }));

    if (showDefaultTemplates) {
      setSortField('positionCount');
      setSortDirection('desc');
    }
  }, [showDefaultTemplates]);

  const handleNextPage = () => {
    setPaginationQuery((prev) => ({ ...prev, page: prev.page + 1 }));
  };

  const handlePreviousPage = () => {
    setPaginationQuery((prev) => ({ ...prev, page: prev.page - 1 }));
  };

  const handleLimitChange = (limit: number) => {
    localStorage.setItem(PAGINATION_LIMIT_KEY, limit.toString());
    setPaginationQuery((prev) => ({ ...prev, before: null, after: null, page: 1, limit }));
  };

  const handleSortChange = (column: SortField) => {
    let newDirection: SortDirection;
    if (sortField === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      newDirection = column === 'name' ? 'asc' : 'desc';
    }

    setSortField(column);
    setSortDirection(newDirection);
    setPaginationQuery((prev) => ({ ...prev, before: null, after: null, page: 1 }));
  };

  const templates = templateResponse?.templates ?? [];

  const handleSearchChange = (value: string) => {
    if (value !== searchQuery) {
      setIsFetching(true);
      setSearchQuery(value);
    }
  };

  const handlePreviewTemplate = (template: QuestionnaireTemplateListResponse): void => {
    setSelectedTemplate(template);
    onOpen();
  };

  const PaginationLimit = ({ limit, onChange }: PaginationLimitProps) => {
    const [value, setValue] = React.useState<number>(() => {
      return limit ?? (Number(localStorage.getItem(PAGINATION_LIMIT_KEY)) || PAGE_SIZE);
    });

    React.useEffect(() => {
      if (limit && value !== limit) {
        setValue(limit);
        localStorage.setItem(PAGINATION_LIMIT_KEY, limit.toString());
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit]);

    function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
      onChange(Number(e.target.value));
    }

    return (
      <Select value={value} onChange={handleChange} size="sm" variant="filled">
        {[10, 25, 50, 100].map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </Select>
    );
  };

  const hasTemplates = templateResponse && templateResponse.templates.length > 0;

  return (
    <Box
      background="white"
      borderRadius={5}
      height="calc(100vh - 130px)"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {isInitialLoading ? (
        <Center py={10}>
          <Spinner size="lg" color="blue.500" />
        </Center>
      ) : (
        <Box position="relative" w="100%" h="100%">
          {!hasTemplates &&
          !searchQuery &&
          !debouncedSearchQuery &&
          !isFetching &&
          (!showDefaultTemplates || !hasDefaultTemplates) ? (
            <QuestionnaireTemplateEmptyView isFetching={isFetching} />
          ) : (
            <QuestionnaireTemplateTableView
              data={templates}
              showDefaultTemplates={showDefaultTemplates}
              onToggleShowDefaultTemplates={setShowDefaultTemplates}
              onCreateNewTemplate={onOpen}
              onOpenTemplate={handlePreviewTemplate}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSortChange={handleSortChange}
              sortDisabled={false /* showDefaultTemplates */}
              pagination={paginationQuery}
              handleNextPage={handleNextPage}
              handlePreviousPage={handlePreviousPage}
              handleLimitChange={handleLimitChange}
              PaginationLimit={PaginationLimit}
              showToggle={hasDefaultTemplates}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              isFetching={isFetching}
            />
          )}
        </Box>
      )}
      <QuestionnaireTemplateModal isOpen={isOpen} onClose={onClose} templates={selectedTemplate ? [selectedTemplate] : []} />
    </Box>
  );
};
